import React from 'react';
import axios from 'axios';
import { FiAlertTriangle, FiRefreshCw, FiHome } from 'react-icons/fi';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorTimestamp: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo, errorTimestamp: Date.now() });
    
    // Log error to backend
    this.logErrorToBackend(error, errorInfo);
  }

  formatUtcPlus7Time = (timestamp) => {
    if (!timestamp) return "";

    try {
      return new Intl.DateTimeFormat("vi-VN", {
        timeZone: "Asia/Bangkok",
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour12: false,
      }).format(new Date(timestamp));
    } catch (err) {
      return "";
    }
  };

  logErrorToBackend = async (error, errorInfo) => {
    try {
      // Determine API URL based on environment
      // Check if we are in production or development
      const isProduction = process.env.NODE_ENV === 'production';
      
      // Default base URL
      let baseUrl = 'http://localhost:3000';
      
      if (isProduction) {
         // In production, use the same origin or configured API URL
         baseUrl = 'https://autowork.com.vn'; // Or whatever the production API URL is
         // Alternatively, if served from same origin:
         // baseUrl = window.location.origin;
      }

      // Try to get API URL from window.REACT_APP_API_URL if injected, or use relative path if proxied
      const apiUrl = process.env.REACT_APP_API_URL || baseUrl;

      await axios.post(`${apiUrl}/api/logs/error`, {
        error: {
          message: error.toString(),
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          name: error.name
        },
        context: {
          url: window.location.href,
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          userId: localStorage.getItem('userId'),
          timestamp: new Date().toISOString()
        }
      });
      console.log('Error logged to backend successfully');
    } catch (err) {
      console.error("ErrorBoundary: Failed to log error", err);
    }
  }

  render() {
    if (this.state.hasError) {
      const errorTime = this.formatUtcPlus7Time(this.state.errorTimestamp);

      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
          <div className="bg-white p-8 rounded-2xl shadow-xl max-w-lg w-full text-center border border-gray-100">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiAlertTriangle className="text-4xl text-red-500" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Đã xảy ra lỗi hệ thống!</h2>
            
            <p className="text-gray-600 mb-8 leading-relaxed">
              Hệ thống đã ghi nhận sự cố và đội ngũ kỹ thuật sẽ khắc phục sớm nhất có thể. 
              Vui lòng tải lại trang hoặc quay về trang chủ.
            </p>
            {errorTime && (
              <p className="text-sm text-gray-500 mb-6">
                Thời điểm ghi nhận (UTC+7): {errorTime}
              </p>
            )}
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <FiRefreshCw className="mr-2" />
                Tải lại trang
              </button>
              
              <button
                 onClick={() => window.location.href = '/'}
                 className="flex items-center justify-center px-6 py-3 bg-white text-gray-700 font-medium rounded-xl border border-gray-300 hover:bg-gray-50 transition-all duration-200"
              >
                <FiHome className="mr-2" />
                Về trang chủ
              </button>
            </div>
            
            {process.env.NODE_ENV === 'development' && (
              <div className="mt-8 bg-gray-100 p-4 rounded-lg text-left overflow-auto max-h-60 text-xs font-mono border border-gray-200">
                 <p className="font-bold text-red-600 mb-2">{this.state.error && this.state.error.toString()}</p>
                 <pre>{this.state.errorInfo && this.state.errorInfo.componentStack}</pre>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
